import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Alert, Box, Button, FormControl, FormLabel, IconButton, Input } from "@mui/joy";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React from "react";
import BuildingKeysDialog, { BuildingKeyRecord, generateBuildingKey } from "../Dialogs/BuildingKeysDialog";
import { Util } from "../Helpers/Util";
import PortalTableLayout from "../components/PortalTableLayout";

interface BuildingKeysListingProps {
	buildingId: string;
}

interface BuildingKey extends BuildingKeyRecord {
	created_by: string;
	created_date: string;
}

const sessionKey = (buildingId: string) => `buildingKeysUnlocked:${buildingId}`;

export default function BuildingKeysListing(props: BuildingKeysListingProps) {
	const [keys, setKeys] = React.useState([] as Array<BuildingKey>);
	const [dialog, setDialog] = React.useState(false);
	const [selectedKey, setSelectedKey] = React.useState<BuildingKey | undefined>();
	const [passwordInput, setPasswordInput] = React.useState("");
	const [storedPassword, setStoredPassword] = React.useState(() => sessionStorage.getItem(sessionKey(props.buildingId)) ?? "");
	const [unlocked, setUnlocked] = React.useState(() => !!sessionStorage.getItem(sessionKey(props.buildingId)));
	const [error, setError] = React.useState("");
	const [loading, setLoading] = React.useState(false);
	const [rowActionId, setRowActionId] = React.useState("");

	React.useEffect(() => {
		const saved = sessionStorage.getItem(sessionKey(props.buildingId)) ?? "";
		setStoredPassword(saved);
		setUnlocked(!!saved);
		setPasswordInput("");
		setError("");
	}, [props.buildingId]);

	React.useEffect(() => {
		if (unlocked && storedPassword) {
			getData(storedPassword);
		} else {
			setKeys([]);
		}
	}, [props.buildingId, unlocked, storedPassword]);

	const getData = (password: string) => {
		setLoading(true);
		fetch("Building/GetBuildingKeys", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ buildingsId: props.buildingId, password }),
		})
			.then((x) => x.json())
			.then((x) => {
				if (x?.error) {
					setError(x.error);
					setUnlocked(false);
					sessionStorage.removeItem(sessionKey(props.buildingId));
					setStoredPassword("");
					setKeys([]);
					return;
				}
				setKeys(x as Array<BuildingKey>);
			})
			.finally(() => setLoading(false));
	};

	const submitPassword = () => {
		setError("");
		setLoading(true);
		fetch("Building/VerifyBuildingKeysPassword", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ password: passwordInput }),
		})
			.then((x) => x.json())
			.then((x) => {
				if (x?.valid) {
					sessionStorage.setItem(sessionKey(props.buildingId), passwordInput);
					setStoredPassword(passwordInput);
					setUnlocked(true);
					setPasswordInput("");
				} else {
					setError("Incorrect password.");
				}
			})
			.finally(() => setLoading(false));
	};

	const closeDialog = () => {
		setDialog(false);
		setSelectedKey(undefined);
		getData(storedPassword);
	};

	const regenerateKey = (key: BuildingKey, event: React.MouseEvent) => {
		event.stopPropagation();
		const newKey = generateBuildingKey();
		setRowActionId(key.building_keys_id);
		fetch("Building/SaveBuildingKey", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				password: storedPassword,
				keyData: {
					building_keys_id: key.building_keys_id,
					buildings_id: props.buildingId,
					key_value: newKey,
					key_type: key.key_type,
					elevator_serial_number: key.elevator_serial_number || null,
				},
			}),
		})
			.then((x) => x.json())
			.then((x) => {
				if (x?.error) {
					setError(x.error);
					return;
				}
				getData(storedPassword);
			})
			.finally(() => setRowActionId(""));
	};

	const deleteKey = (key: BuildingKey, event: React.MouseEvent) => {
		event.stopPropagation();
		if (!window.confirm("Are you sure you want to delete this key? This cannot be undone.")) return;

		setRowActionId(key.building_keys_id);
		fetch("Building/DeleteBuildingKey", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				password: storedPassword,
				buildingKeysId: key.building_keys_id,
			}),
		})
			.then((x) => x.json())
			.then((x) => {
				if (x?.error) {
					setError(x.error);
					return;
				}
				getData(storedPassword);
			})
			.finally(() => setRowActionId(""));
	};

	if (!unlocked) {
		return (
			<Box sx={{ maxWidth: 420, display: "flex", flexDirection: "column", gap: 2 }}>
				<FormControl>
					<FormLabel>Building Keys Password</FormLabel>
					<Input
						type="password"
						value={passwordInput}
						onChange={(e) => setPasswordInput(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") submitPassword();
						}}
					/>
				</FormControl>
				{error && <Alert color="danger">{error}</Alert>}
				<Button loading={loading} onClick={submitPassword}>
					Submit
				</Button>
			</Box>
		);
	}

	return (
		<>
			{dialog && (
				<BuildingKeysDialog
					buildingId={props.buildingId}
					password={storedPassword}
					keyRecord={selectedKey}
					close={closeDialog}
					onSaved={() => getData(storedPassword)}
				/>
			)}
			{error && (
				<Alert color="danger" sx={{ mb: 1 }}>
					{error}
				</Alert>
			)}
			<PortalTableLayout
				fillHeight={false}
				toolbar={
					<Box sx={{ p: 1, alignSelf: "flex-start" }}>
						<Button
							sx={{ minWidth: 120 }}
							startDecorator={<AddIcon />}
							onClick={() => {
								setSelectedKey(undefined);
								setDialog(true);
							}}>
							Add Key
						</Button>
					</Box>
				}>
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							<TableCell>Key</TableCell>
							<TableCell>Key Type</TableCell>
							<TableCell>Elevator Serial</TableCell>
							<TableCell>Created By</TableCell>
							<TableCell>Date Created</TableCell>
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{keys.map((x) => (
							<TableRow
								key={x.building_keys_id}
								hover
								sx={{ cursor: "pointer" }}
								onClick={() => {
									setSelectedKey(x);
									setDialog(true);
								}}>
								<TableCell>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										{x.key_value}
										<IconButton
											size="sm"
											variant="plain"
											title="Copy key"
											onClick={(e) => {
												e.stopPropagation();
												navigator.clipboard.writeText(x.key_value);
											}}>
											<ContentCopyIcon fontSize="small" />
										</IconButton>
									</Box>
								</TableCell>
								<TableCell>{x.key_type}</TableCell>
								<TableCell>{x.elevator_serial_number || "—"}</TableCell>
								<TableCell>{x.created_by}</TableCell>
								<TableCell>{Util.Formatter(x.created_date, "D")}</TableCell>
								<TableCell align="right">
									<IconButton
										size="sm"
										variant="plain"
										title="Regenerate key"
										disabled={rowActionId === x.building_keys_id}
										onClick={(e) => regenerateKey(x, e)}>
										<RefreshIcon fontSize="small" />
									</IconButton>
									<IconButton
										size="sm"
										variant="plain"
										color="danger"
										title="Delete key"
										disabled={rowActionId === x.building_keys_id}
										onClick={(e) => deleteKey(x, e)}>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</TableCell>
							</TableRow>
						))}
						{!loading && keys.length === 0 && (
							<TableRow>
								<TableCell colSpan={6}>No keys yet.</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</PortalTableLayout>
		</>
	);
}
